import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { StatusVariant } from '@factorialco/f0-react'
import { ResourceHeader } from '@factorialco/f0-react/dist/experimental'
import {
  Add,
  Envelope,
  ExternalLink,
  LayersFront,
  Pencil,
  WhatsappChat,
} from '@factorialco/f0-react/icons/app'
import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import copy from 'copy-to-clipboard'
import {
  AtsCandidateHeaderFragment,
  AtsCreatedApplicationsfromCandidatesFragment,
} from 'generated/resources/graphql'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import SafeLocalStorage from 'modules/core/lib/SafeLocalStorage'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'
import { isAbsoluteURL } from 'modules/core/lib/isAbsoluteURL'
import { normalizeAndFormatPhoneNumber } from 'modules/core/lib/phoneNumber'
import app from 'modules/core/store/models/app'

import { DeleteCandidateModal } from 'modules/ats/components/DeleteCandidateModal/DeleteCandidateModal'
import { OverwriteDataConsentDateModal } from 'modules/ats/components/OverwriteDataConsentDateModal/OverwriteDataConsentDateModal'
import { AddCandidatesToJobPostingModal } from 'modules/ats/components/candidates/AddCandidateToJobPostingModal'
import { TalentPoolConfirmationModal } from 'modules/ats/components/candidates/TalentPoolConfirmationModal'
import { ellipsisCandidateData } from 'modules/ats/lib/utils/candidate'
import { useUpdateTalentPool } from 'modules/ats/lib/utils/useUpdateTalentPool'
import { EditCandidateModal } from 'modules/ats/pages/Candidates/ShowCandidate/CandidateDetailPage/components/EditCandidateModal'

interface Props {
  candidateData: AtsCandidateHeaderFragment
  isDataConsentLimitEnabled: boolean
  isWhatsappFeatureEnabled: boolean
  isWhatsappInstalled: boolean
}

export const CandidateHeader: React.FC<Props> = ({
  candidateData,
  isWhatsappFeatureEnabled,
  isDataConsentLimitEnabled,
  isWhatsappInstalled,
}) => {
  const [openedModal, setOpenedModal] = useState<
    | 'add-to-job-posting'
    | 'edit'
    | 'overwrite-data-consent-date'
    | 'delete'
    | 'talent-pool'
    | undefined
  >(undefined)

  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const {
    id,
    applications,
    atsFiles,
    email,
    firstName,
    fullName,
    inactiveSince,
    lastName,
    personalUrl,
    phoneNumber,
    talentPool,
    consentToTalentPool,
  } = candidateData

  const applicationWithAvatar = applications.find((application) =>
    application.files.some((file) => file.name === 'photo')
  )
  const avatar =
    applicationWithAvatar?.files.find((file) => file.name === 'photo') ||
    atsFiles.find((file) => file.category === 'photo')?.file

  const active = !inactiveSince
  const archived = !active && !talentPool
  const inTalentPool = talentPool && !active

  const [statusLabel, statusColor]: [string, StatusVariant] = (() => {
    if (archived) {
      return [i18n.t('ats.candidate_status.archived'), 'neutral']
    }
    if (active) {
      return [i18n.t('ats.candidate_status.active'), 'positive']
    }
    if (inTalentPool) {
      return [i18n.t('ats.candidate_status.talent_pool'), 'info']
    }
    return ['', 'neutral']
  })()

  let personalUrlLink = null
  if (personalUrl) {
    personalUrlLink = isAbsoluteURL(personalUrl) ? personalUrl : `https://${personalUrl}`
  }

  const copyUrl = async (url: string) => {
    if (copy(url)) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.copied'),
      })
    }
  }

  const onClickOpenWhatsappChat = () => {
    track(trackingEvents.ats.openWhatsappChatWithCandidate)
    window.open(whatsappUrl, '_blank', 'noopener,noreferrer')
  }

  const { updateTalentPoolValue } = useUpdateTalentPool({
    candidateId: id,
    currentTalentPoolValue: talentPool,
  })

  const onClickTalentPool = async () => {
    const dontShowConfirmation =
      consentToTalentPool ||
      SafeLocalStorage.getItem(LOCAL_STORAGE_KEYS.atsTalentPoolConsentMessage, JSON.parse)

    if (talentPool || dontShowConfirmation) {
      await updateTalentPoolValue()
    } else {
      setOpenedModal('talent-pool')
    }
  }

  const phoneNumberFormatted = normalizeAndFormatPhoneNumber({
    phoneNumber: phoneNumber,
  })
  const isWhatsappEnabled =
    (isWhatsappInstalled || isWhatsappFeatureEnabled) && !!phoneNumberFormatted

  const whatsappUrl =
    isWhatsappEnabled && phoneNumberFormatted ? `https://wa.me/${phoneNumberFormatted}` : undefined

  return (
    <>
      <ResourceHeader
        title={fullName}
        avatar={{ type: 'person', firstName: firstName, lastName: lastName, src: avatar?.url }}
        status={{
          label: i18n.t('ats.candidate_details.header.status_label'),
          text: statusLabel,
          variant: statusColor,
        }}
        primaryAction={{
          label: i18n.t('ats.candidate_details.add_to_job_opening'),
          icon: Add,
          onClick: () => setOpenedModal('add-to-job-posting'),
        }}
        secondaryActions={[
          {
            label: i18n.t('common.edit'),
            icon: Pencil,
            onClick: () => setOpenedModal('edit'),
          },
        ]}
        otherActions={[
          {
            label: talentPool
              ? i18n.t('ats.candidate_details.remove_from_talent_pool')
              : i18n.t('ats.candidate_details.add_to_talent_pool'),
            onClick: () => onClickTalentPool(),
          },
          isDataConsentLimitEnabled && {
            label: i18n.t('ats.candidate_details.overwrite_consent_date_title'),
            onClick: () => setOpenedModal('overwrite-data-consent-date'),
          },
          {
            label: i18n.t('ats.candidates.delete_modal.open_modal_button'),
            onClick: () => setOpenedModal('delete'),
            critical: true,
          },
        ].filter(Boolean)}
        metadata={[
          !!email && {
            label: i18n.t('ats.candidate_details.header.email_label'),
            value: {
              type: 'text',
              content: ellipsisCandidateData(email) || '',
            },
            actions: [
              {
                label: i18n.t('ats.candidate_details.header.send_email'),
                icon: Envelope,
                onClick: () => window.open(`mailto:${email}`, '_blank', 'noopener,noreferrer'),
              },
              {
                label: i18n.t('common.copy'),
                icon: LayersFront,
                onClick: () => copyUrl(email),
              },
            ],
          },
          !!phoneNumber && {
            label: i18n.t('ats.candidate_details.header.phone_label'),
            value: { type: 'text', content: phoneNumber },
            actions: [
              whatsappUrl && {
                label: i18n.t('ats.candidate_details.header.chat_on_whatsapp'),
                icon: WhatsappChat,
                onClick: () => onClickOpenWhatsappChat(),
              },
              {
                label: i18n.t('ats.candidate_details.header.call_from_my_phone'),
                icon: ExternalLink,
                onClick: () => null, // TODO: phone – showCallFromMyPhone functionality
              },
            ].filter(Boolean),
          },
          !!personalUrlLink && {
            label: i18n.t('ats.candidate_details.header.link_label'),
            value: {
              type: 'text',
              content: ellipsisCandidateData(personalUrlLink) || '',
            },
            actions: [
              {
                label: i18n.t('ats.candidate_details.header.navigate_to_link'),
                icon: ExternalLink,
                onClick: () => window.open(personalUrlLink, '_blank', 'noopener,noreferrer'),
              },
              {
                label: i18n.t('common.copy'),
                icon: LayersFront,
                onClick: () => copyUrl(personalUrlLink),
              },
            ],
          },
        ]}
      />

      {openedModal === 'add-to-job-posting' && (
        <AddCandidatesToJobPostingModal
          candidateIds={[id]}
          onSuccess={(returnedApplications: AtsCreatedApplicationsfromCandidatesFragment[]) => {
            const application = returnedApplications[0]
            application?.id &&
              navigate(
                navigation.ats.jobs.jobsId.applications.applicationDetail.fullPath({
                  jobId: application.jobPosting.id,
                  application_id: application.id,
                })
              )
          }}
          onClose={() => setOpenedModal(undefined)}
        />
      )}

      {openedModal === 'edit' && (
        <EditCandidateModal candidate={candidateData} onClose={() => setOpenedModal(undefined)} />
      )}
      {openedModal === 'overwrite-data-consent-date' && (
        <OverwriteDataConsentDateModal candidateId={id} onClose={() => setOpenedModal(undefined)} />
      )}
      {openedModal === 'delete' && (
        <DeleteCandidateModal
          candidateIds={[id]}
          onClose={() => {
            setOpenedModal(undefined)
            navigate(navigation.ats.candidates.fullPath())
          }}
        />
      )}
      {openedModal === 'talent-pool' && (
        <TalentPoolConfirmationModal candidateId={id} onClose={() => setOpenedModal(undefined)} />
      )}
    </>
  )
}
